import { useEffect } from 'react'
import { Link } from 'react-router'
import { TicketQr } from '../../molecules/ticket/TicketQr.tsx'

type DoorPassLine = {
	label: string
	status: string
	tone: 'good' | 'warn' | 'bad' | 'muted'
}

/** the wallet's status colours, dark enough for the white sheet */
const TONE: Record<DoorPassLine['tone'], string> = {
	good: 'text-[#245b2c]',
	warn: 'text-[#6b4a07]',
	bad: 'text-[#8c2716]',
	muted: 'text-black/60',
}

/**
 * The dedicated door surface: one QR code for all of one concert's tickets
 * (the buyer's door pass), large on a high-contrast sheet, with the tickets
 * it lets in listed below. The door scans once and sees the whole party.
 * The QR generates lazily (see TicketQr), so nothing costs until it opens.
 */
export function DoorPassDialog({
	eventTitle,
	passHash,
	lines,
	open,
	onClose,
}: {
	eventTitle: string
	passHash: string
	lines: DoorPassLine[]
	open: boolean
	onClose: () => void
}) {
	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [open, onClose])

	if (!open) return null

	const count = lines.length
	return (
		<div className="modal modal-open" role="dialog" aria-modal="true">
			<div className="modal-box max-w-md bg-white text-black">
				<h2 className="text-xl font-bold">{eventTitle}</h2>
				<p className="mt-1 text-sm opacity-70">
					{count === 1
						? 'Vis QR-koden i døra.'
						: `Éin QR-kode for alle ${count} billettane — vis han i døra.`}
				</p>
				<div className="mt-4 flex justify-center">
					<TicketQr
						hash={passHash}
						label={`QR-kode for ${count === 1 ? 'billetten' : `${count} billettar`}`}
						className="h-60 w-60 [image-rendering:pixelated]"
					/>
				</div>
				<ul className="mt-4 divide-y divide-black/10 border-y border-black/10">
					{lines.map((line, i) => (
						<li
							key={i}
							className="flex items-baseline justify-between gap-4 py-2"
						>
							<span className="font-semibold">{line.label}</span>
							<span
								className={`text-xs font-bold tracking-wide uppercase ${TONE[line.tone]}`}
							>
								{line.status}
							</span>
						</li>
					))}
				</ul>
				<p className="mt-3 text-sm opacity-70">
					Skru opp lysstyrken for enklare skanning.
				</p>
				<div className="modal-action items-center justify-between">
					<Link to={`/t/${passHash}`} className="link text-sm">
						Opne som eiga side
					</Link>
					<button type="button" className="btn" onClick={onClose}>
						Lukk
					</button>
				</div>
			</div>
			<button
				type="button"
				aria-label="Lukk dialogen"
				className="modal-backdrop"
				onClick={onClose}
			/>
		</div>
	)
}
